import Axios from "axios";
import {handleAjaxSuccess, swalConfirm} from "../../../helpers/swalHelper";
import {Calendar} from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import {initCustomFlatPickerInput} from "../../../helpers/inputs/flatPicker/flatPicker_timeonly_custom";
import {ServerModalV3} from "../../../helpers/modals/ClassBased/ServerModalV3";
import {loading, stopLoading} from "../../../helpers/loading";
import handleAjaxError from "../../../helpers/handleAjaxError";
import {clearFeedbacksByName, getInputsByNameV2} from "../../../helpers/forms";
import {getSingleInputByNameV3, getSingleInputElementByNameV3} from "../../../helpers/forms/formInputsHelpers";
import bootstrapPlugin from '@fullcalendar/bootstrap';

/**
 * Manages both rehearsals and shows views, and probably any
 * future event like type of view
 */
function initProductionRehearsalsIndex() {
	const calendarContainer = document.getElementById("calendar");


// noinspection JSUnusedGlobalSymbols,JSCheckFunctionSignatures
	const calendar = new Calendar(
		calendarContainer,
		{
			plugins: [dayGridPlugin, interactionPlugin, bootstrapPlugin],
			themeSystem: 'bootstrap',
			eventDisplay: 'block',

			headerToolbar: {
				left: 'prev',
				center: 'title',
				right: 'next',
			},

			// Custom html for event display
			eventContent: data => {
				const event = data.event;

				return {
					html: `
						<div>
							<div class="event-range text-monospace">${event.title}</div>
							<div class="timeframe small">${event.extendedProps.timeframe}</div>
							<div class="notes small">${event.extendedProps.truncatedDescription}</div>
						</div>
					`
				};
			},

			// Fetch events from the server
			events: function (fetchInfo, successCallback, failureCallback) {
				Axios.get(window.routes.fetchEvents)
					.then(function (response) {
						successCallback(response.data); // Pass events to FullCalendar
					})
					.catch(function (error) {
						console.error("Error fetching events:", error);
						failureCallback(error);
					});
			},

			// Handle date click to add a new event
			dateClick: function (info) {
				// Get the clicked date
				const clickedDate = info.dateStr;

				// Add event date to modal route
				const modalRoute = new URL(window.routes.createEventModal);
				modalRoute.searchParams.set('date', clickedDate)

				displayEventModal(modalRoute.toString());
			},

			// Handle event click to edit an existing event
			eventClick: function (info) {
				const modalRoute = info.event.extendedProps.getUpdateModalRoute;

				displayEventModal(modalRoute);
			},
		});

	calendar.render();

	// Methods

	function displayEventModal(modalRoute) {
		new ServerModalV3(
			modalRoute.toString(),
			{
				init(modal) {
					// Init inputs
					const startTimePicker =
						modal.modalContainer.querySelector(
							".start-time-picker"
						);
					const endTimePicker =
						modal.modalContainer.querySelector(".end-time-picker");

					[startTimePicker, endTimePicker].forEach(input => {
						initCustomFlatPickerInput(input, {
							noCalendar: true,
							enableTime: true,
							static: true,
						})
					})

					// Init clear buttons
					modal.modalContainer.querySelectorAll('.clear-date-button').forEach(button => {
						// Retrieve related input
						const relatedInput = button.closest('.form-group').querySelector('input');

						// Clear flatpickr instance on click
						button.addEventListener('click', () => {
							relatedInput._flatpickr.clear();
						})
					})

					// Init submit button
					modal.modalContainer
						.querySelector(".submit-modal-button")
						.addEventListener(
							"click",
							() => {
								// Send Axios POST request to store the event
								handleSubmitEventRequest(modal);
							});

					// Init delete button
					const deleteButton = modal.modalContainer
						.querySelector(".delete-event-button");

					if (deleteButton) {
						deleteButton.addEventListener(
							"click",
							() => {
								// Send Axios POST request to store the event
								handleDeleteRequest(deleteButton.dataset.route, modal);
							});
					}
				},
			});
	}

	/**
	 * @param {ServerModalV3} modal
	 */
	function handleSubmitEventRequest(modal) {
		loading();

		// Retrieve data from modal
		const data = getInputsByNameV2(modal.modalContainer, [
			'event_type',
			'notes',
		]);

		const submitRoute = getSingleInputByNameV3(modal.modalContainer, 'submit_route');

		// Format start and end times before sending them to the server
		const eventDate = getSingleInputElementByNameV3(modal.modalContainer, 'date').dataset.htmlDate;

		const startTime = getSingleInputByNameV3(modal.modalContainer, 'start_time');
		if (startTime) {
			data.start_time = `${eventDate} ${startTime}`;
		} else {
			data.start_time = undefined;
		}

		const endTime = getSingleInputByNameV3(modal.modalContainer, 'end_time');
		if (endTime) {
			data.end_time = `${eventDate} ${endTime}`;
		} else {
			data.end_time = undefined;
		}

		clearFeedbacksByName(modal.modalContainer);

		Axios.post(submitRoute, data)
			.then(function (response) {
				calendar.refetchEvents(); // Reload the calendar events
				handleAjaxSuccess(response, () => {
					// Hide modal
					modal.hide();
				});
			})
			.catch(error => {
				handleAjaxError(error, modal.modalContainer)
			})
			.finally(stopLoading);
	}

	/**
	 * @param {string} deleteRoute
	 * @param {ServerModalV3} sourceModal
	 */
	function handleDeleteRequest(deleteRoute, sourceModal = null) {
		swalConfirm({
			title: 'Are you sure you want to delete this event?',
			text: 'This action cannot be undone',
		}).then(result => {
			if (result.isConfirmed) {
				loading();

				Axios.post(deleteRoute)
					.then(response => {
						handleAjaxSuccess(response, () => {
							// Hide modal
							if (sourceModal) {
								sourceModal.hide();
							}

							// Refresh calendar
							calendar.refetchEvents();
						})
					})
					.catch(handleAjaxError)
					.finally(stopLoading)
			}
		})
	}
}

export {initProductionRehearsalsIndex};
